// TODO: needs backend persistence via /customers/{id}/config post-launch

export const SETUP_STORAGE_KEY = "ns_setup_v1";

export type SetupStepId = 1 | 2 | 3 | 4 | 5;

export type SetupStepData = {
  business?: {
    businessName?: string;
    industry?: string;
    hours?: BusinessHoursSchedule;
  };
  teach?: {
    skipped?: boolean;
    urlImported?: boolean;
    documentUploaded?: boolean;
  };
  receptionist?: {
    agentName?: string;
  };
  team?: {
    deferredLater?: boolean;
  };
  connect?: {
    subStep?: number;
    forwardingMode?: "every_call" | "missed_only";
    carrierId?: string | null;
    concierge?: boolean;
    verified?: boolean;
    manuallyVerified?: boolean;
    listeningStartedAt?: string | null;
  };
};

export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type DayHours = {
  open: string;
  close: string;
  closed?: boolean;
};

export type BusinessHoursSchedule = Record<Weekday, DayHours>;

export const WEEKDAYS: { id: Weekday; label: string }[] = [
  { id: "mon", label: "Monday" },
  { id: "tue", label: "Tuesday" },
  { id: "wed", label: "Wednesday" },
  { id: "thu", label: "Thursday" },
  { id: "fri", label: "Friday" },
  { id: "sat", label: "Saturday" },
  { id: "sun", label: "Sunday" },
];

export const DEFAULT_DAY_HOURS: DayHours = { open: "09:00", close: "17:00" };

export function createDefaultBusinessHours(): BusinessHoursSchedule {
  return WEEKDAYS.reduce(
    (acc, { id }) => {
      acc[id] = { ...DEFAULT_DAY_HOURS };
      return acc;
    },
    {} as BusinessHoursSchedule,
  );
}

export function getOnboardingBusinessPrefill(): {
  businessName?: string;
  industry?: string;
} {
  try {
    const raw = localStorage.getItem("ns_onboarding_v1");
    if (!raw) return {};
    const ob = JSON.parse(raw) as Record<string, unknown>;
    const step2 = ob.step2 as Record<string, unknown> | undefined;
    const step1 = ob.step1 as Record<string, unknown> | undefined;
    const businessName = String(
      step2?.businessName ?? step1?.businessName ?? "",
    ).trim();
    const industry = String(ob.businessType ?? "").trim();
    return {
      businessName: businessName || undefined,
      industry: industry || undefined,
    };
  } catch {
    return {};
  }
}

export type SetupData = {
  currentStep: SetupStepId;
  completedSteps: SetupStepId[];
  skipped?: boolean;
  completedAt?: string;
  bannerDismissed?: boolean;
  steps: SetupStepData;
};

export function isSetupWizardEnabled(): boolean {
  const val = import.meta.env.VITE_SETUP_WIZARD;
  return val === "true" || val === "1";
}

/** Post-login / post-checkout destination when the setup wizard flag is on. */
export function getPostAuthDestination(customerId: string): "/setup" | "/dashboard" {
  if (isSetupWizardEnabled() && !isSetupComplete(customerId)) {
    return "/setup";
  }
  return "/dashboard";
}

export function shouldShowSetupBanner(customerId: string): boolean {
  if (!isSetupWizardEnabled()) return false;
  if (isSetupComplete(customerId)) return false;
  const data = loadSetupState(customerId);
  return !data?.bannerDismissed;
}

export function dismissSetupBanner(customerId: string): void {
  const data = getOrCreateSetupState(customerId);
  saveSetupState(customerId, { ...data, bannerDismissed: true });
}

type SetupStore = Record<string, SetupData>;

function getStore(): SetupStore {
  try {
    const raw = localStorage.getItem(SETUP_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as SetupStore)
      : {};
  } catch {
    return {};
  }
}

function saveStore(store: SetupStore): void {
  localStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(store));
}

export function loadSetupState(customerId: string): SetupData | null {
  return getStore()[customerId] ?? null;
}

export function getOrCreateSetupState(customerId: string): SetupData {
  const existing = loadSetupState(customerId);
  if (existing) return existing;

  const fresh: SetupData = {
    currentStep: 1,
    completedSteps: [],
    steps: {},
  };
  saveSetupState(customerId, fresh);
  return fresh;
}

export function saveSetupState(customerId: string, data: SetupData): void {
  const store = getStore();
  store[customerId] = data;
  saveStore(store);
}

export function isSetupComplete(customerId: string): boolean {
  const data = loadSetupState(customerId);
  if (!data) return false;
  return Boolean(data.completedAt) || data.completedSteps.length >= 5;
}

/** Alias for readability at call sites. */
export const isComplete = isSetupComplete;

export function markSetupSkipped(customerId: string): SetupData {
  const data = getOrCreateSetupState(customerId);
  const updated: SetupData = { ...data, skipped: true };
  saveSetupState(customerId, updated);
  return updated;
}

export function markSetupComplete(customerId: string): SetupData {
  const data = getOrCreateSetupState(customerId);
  const updated: SetupData = {
    ...data,
    completedSteps: [1, 2, 3, 4, 5],
    currentStep: 5,
    completedAt: new Date().toISOString(),
  };
  saveSetupState(customerId, updated);
  return updated;
}

export const SETUP_STEP_LABELS: Record<SetupStepId, string> = {
  1: "Your business",
  2: "Teach your AI",
  3: "Meet your receptionist",
  4: "Your team",
  5: "Connect your line",
};

export const SETUP_STEP_LINKS: Partial<Record<SetupStepId, string>> = {
  2: "/knowledge-base",
  3: "/phone-system-app",
  4: "/team",
  5: "/phone-system-app?tab=agent",
};
